/**
 * Utilidades de color sin React: validar hex y calcular contraste (WCAG).
 *
 * Todo color que llega desde `localStorage` o desde un campo pasa por `normalizarHex`: solo
 * sale `#rrggbb`, así nunca se inyecta texto arbitrario en una variable CSS.
 */

type Rgb = [number, number, number];

const HEX_6 = /^#[0-9a-f]{6}$/i;
const HEX_3 = /^#[0-9a-f]{3}$/i;

export const TEXTO_OSCURO = '#141a17';
export const TEXTO_CLARO = '#ffffff';

/** Devuelve `#rrggbb` en minúsculas, o `null` si el valor no es un hex válido de 3 o 6 dígitos. */
export function normalizarHex(valor: unknown): string | null {
  if (typeof valor !== 'string') return null;
  const limpio = valor.trim().toLowerCase();
  if (HEX_6.test(limpio)) return limpio;
  if (HEX_3.test(limpio)) {
    return `#${[...limpio.slice(1)].map((c) => c + c).join('')}`;
  }
  return null;
}

function aRgb(hex: string): Rgb {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function aHex(rgb: Rgb): string {
  const partes = rgb.map((v) =>
    Math.round(Math.min(255, Math.max(0, v)))
      .toString(16)
      .padStart(2, '0'),
  );
  return `#${partes.join('')}`;
}

/** Luminancia relativa (0 negro, 1 blanco) según WCAG 2. */
export function luminancia(hex: string): number {
  const [r, g, b] = aRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Razón de contraste entre dos colores (1 a 21). */
export function contraste(a: string, b: string): number {
  const la = luminancia(a);
  const lb = luminancia(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Texto (claro u oscuro) con mejor contraste sobre el fondo dado. */
export function textoSobre(fondo: string): string {
  return textoSobreVarios([fondo]);
}

/** Texto con mejor contraste en el peor de varios fondos (p. ej. los dos extremos de un degradado). */
export function textoSobreVarios(fondos: string[]): string {
  const peor = (texto: string) => Math.min(...fondos.map((f) => contraste(texto, f)));
  const preferido = peor(TEXTO_OSCURO) >= peor(TEXTO_CLARO) ? TEXTO_OSCURO : TEXTO_CLARO;
  // En grises medios ni el texto de marca alcanza 4,5: ahí vale el negro puro.
  return peor(preferido) < 4.5 && peor('#000000') > peor(preferido) ? '#000000' : preferido;
}

export function esOscuro(fondo: string): boolean {
  return textoSobre(fondo) === TEXTO_CLARO;
}

/** Mezcla `a` con `b`; `t` = 0 deja `a`, `t` = 1 deja `b`. */
export function mezclar(a: string, b: string, t: number): string {
  const ra = aRgb(a);
  const rb = aRgb(b);
  return aHex([0, 1, 2].map((i) => (ra[i] ?? 0) * (1 - t) + (rb[i] ?? 0) * t) as Rgb);
}

/**
 * Acerca `color` a `haciaColor` hasta que tenga al menos `minimo` de contraste sobre todos los
 * `fondos`. Sirve para acentos y textos suaves que deben leerse sobre superficies arbitrarias.
 */
export function asegurarContraste(
  color: string,
  fondos: string[],
  haciaColor: string,
  minimo = 4.5,
): string {
  for (let paso = 0; paso <= 20; paso++) {
    const candidato = mezclar(color, haciaColor, paso / 20);
    if (fondos.every((f) => contraste(candidato, f) >= minimo)) return candidato;
  }
  return haciaColor;
}
